<template>
  <v-container fluid>
    <v-card class="elevation-3">
      <v-card-title class="body-1">
        {{
          zone.ID > 0
            ? $vuetify.lang.t('$vuetify.SETTINGS.EDIT_ZONE')
            : $vuetify.lang.t('$vuetify.SETTINGS.ADD_NEW_ZONE')
        }}
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeForm()">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-toolbar
        v-if="loading.state && loading.type === 'getZone'"
        class="elevation-0"
      >
        <v-spacer></v-spacer>
        <v-progress-circular
          indeterminate
          color="secondary"
        ></v-progress-circular>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-row class="pl-4">
        <v-col sm="4">
          <v-text-field
            v-model="name"
            :label="$vuetify.lang.t('$vuetify.NAME')"
            :placeholder="$vuetify.lang.t('$vuetify.NAME')"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
        <v-col sm="4">
          <v-text-field
            v-model="code"
            :label="$vuetify.lang.t('$vuetify.CODE')"
            :placeholder="$vuetify.lang.t('$vuetify.CODE')"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <div class="pl-4 d-flex align-center">
        <v-btn color="white" small class="mr-3 px-0">
          <label class="btn btn-xs btn-primary">
            <input
              type="file"
              accept="image/x-png"
              hidden
              @change="chooseIcon"
            />
            <v-icon class="success--text">mdi-plus</v-icon>
            {{ $vuetify.lang.t('$vuetify.SETTINGS.CHOOSE_ICON') }}
          </label>
        </v-btn>
        <img v-if="image" :src="image.url" :alt="image.file.name" />
      </div>
      <v-card-actions>
        <v-btn
          v-if="id > 0"
          class="btn-delete"
          color="error"
          @click="removeZone()"
        >
          {{ $vuetify.lang.t('$vuetify.DELETE') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          class="btn-save secondary--text"
          color="primary"
          :disabled="saveButtonDisabled"
          @click="createZone()"
        >
          {{ $vuetify.lang.t('$vuetify.SAVE') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Zone from '@/models/zone'

export default {
  name: 'Zone',
  props: {
    id: String
  },
  computed: {
    ...mapGetters('app', ['loading']),
    ...mapGetters('zones', ['zone', 'icon']),
    name: {
      get() {
        return this.zone ? this.zone.Name : ''
      },
      set(value) {
        this.setZoneValue({
          key: 'Name',
          value
        })
      }
    },
    code: {
      get() {
        return this.zone ? this.zone.Code : ''
      },
      set(value) {
        this.setZoneValue({
          key: 'Code',
          value: value.toUpperCase()
        })
      }
    },
    image: {
      get() {
        const { icon, name } = this

        if (icon) {
          return icon
        }

        if (this.id > 0 && name) {
          const environment =
            process.env.NODE_ENV === 'production' ? 'live' : 'test'
          return {
            file: { name },
            url: `https://barking-public.ams3.digitaloceanspaces.com/images/${environment}/${name.toUpperCase()}.png`
          }
        }
        return null
      },
      set(value) {
        this.setIcon(value)
      }
    },
    saveButtonDisabled: {
      get() {
        return !(this.name || this.code)
      }
    }
  },
  created() {
    const { id } = this

    if (id > 0) {
      this.getZone(id)
    }
  },
  methods: {
    ...mapActions('zones', ['getZone', 'saveZone', 'deleteZone']),
    ...mapMutations('zones', ['setZoneValue', 'setIcon']),
    closeForm() {
      this.image = null
      this.$store.commit('zones/setZone', new Zone().getObject())
      this.$router.replace('/settings/operator')
    },
    chooseIcon({ target: { files } }) {
      const reader = new FileReader()
      const file = files[0]
      reader.onloadend = ({ target: { result } }) => {
        this.image = {
          file,
          url: URL.createObjectURL(file),
          result
        }
      }
      reader.readAsDataURL(file)
    },
    createZone() {
      const payload = {
        zone: this.zone,
        icon: this.icon
      }
      this.saveZone(payload)
      this.closeForm()
    },
    removeZone() {
      this.$confirm(this.$vuetify.lang.t('$vuetify.CAN_NOT_BE_UNDONE'), {
        title: this.$vuetify.lang.t('$vuetify.ARE_YOU_SURE'),
        color: 'error',
        icon: 'mdi-alert',
        persistent: true,
        buttonTrueText: this.$vuetify.lang.t('$vuetify.YES'),
        buttonFalseText: this.$vuetify.lang.t('$vuetify.CANCEL')
      }).then(async option => {
        if (option) {
          this.deleteZone(this.zone)
          this.closeForm()
        }
      })
    }
  }
}
</script>

<style scoped>
label {
  padding: 0 12px;
}

img {
  height: 32px;
}

.btn-delete {
  position: fixed;
  right: 136px;
  bottom: 36px;
}

.btn-save {
  position: fixed;
  right: 36px;
  bottom: 36px;
}
</style>
